import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Grid,
	Typography,
	Tooltip,
	Rating,
} from "@mui/material";
import { fetchDealerScore } from "../../api/dealers";

const pctToStars = (value) =>
	typeof value === "number"
		? Math.round((value / 20) * 10) / 10
		: null;

const scoreColor = (scorePct = 0) => {
	if (scorePct >= 80) return "#57e32c";
	if (scorePct >= 60) return "#b7dd29";
	if (scorePct >= 40) return "#ffe234";
	if (scorePct >= 20) return "#ffa534";
	return "#ff4545";
};

function formatLabel(value) {
	return value ?? "—";
}

export default function DealerOverview({ dealer, dealerId }) {
	const [score, setScore] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!dealerId) {
			setScore(null);
			setError(null);
			return;
		}
		const controller = new AbortController();
		setLoading(true);
		setError(null);
		fetchDealerScore(dealerId, controller.signal)
			.then((data) => {
				setScore(data);
				setError(null);
			})
			.catch((err) => {
				if (controller.signal.aborted) return;
				setScore(null);
				setError(err?.message || "Unable to load dealer score");
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});
		return () => controller.abort();
	}, [dealerId]);

	const breakdown = score?.breakdown ?? {};
	const scorePct =
		typeof breakdown.composite_vehicle_score === "number"
			? breakdown.composite_vehicle_score
			: null;
	const stars = pctToStars(scorePct);
	const segments = useMemo(
		() => [
			{ key: "Performance", value: breakdown.performance_score },
			{ key: "Health", value: breakdown.health_score },
			{ key: "Energy", value: breakdown.energy_score },
		],
		[breakdown.performance_score, breakdown.health_score, breakdown.energy_score]
	);

	const headerItems = [
		{ label: "Fleet size", value: dealer?.vehicle_count },
		{ label: "City", value: [dealer?.city, dealer?.state].filter(Boolean).join(", ") },
		{ label: "Contact", value: dealer?.contact_name },
	];

	return (
		<Card variant="outlined">
			<CardContent>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
						<Typography variant="h5" fontWeight={700}>
							{dealer?.name ?? "Dealer"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{[dealer?.city, dealer?.state, dealer?.country]
								.filter(Boolean)
								.join(", ")}
						</Typography>
					</Box>
					<Grid container spacing={2}>
						{headerItems.map(({ label, value }) => (
							<Grid item xs={12} md={4} key={label}>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 0.5,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										{label}
									</Typography>
									<Typography variant="body1" fontWeight={600}>
										{formatLabel(value)}
									</Typography>
								</Box>
							</Grid>
						))}
					</Grid>
					<Divider />
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography variant="subtitle1" fontWeight={600}>
							Dealer score
						</Typography>
						{loading ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									minHeight: 120,
								}}
							>
								<CircularProgress size={24} />
							</Box>
						) : error ? (
							<Typography variant="body2" color="error">
								{error}
							</Typography>
						) : stars == null ? (
							<Typography variant="body2" color="text.secondary">
								Score unavailable.
							</Typography>
						) : (
							<Box
								sx={{
									display: "flex",
									flexDirection: { xs: "column", sm: "row" },
									alignItems: "center",
									gap: 2,
								}}
							>
								<Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
									<Tooltip
										arrow
										title={
											<Box
												sx={{
													display: "grid",
													gridTemplateColumns: "auto auto",
													columnGap: 1,
													rowGap: 0.25,
												}}
											>
												{segments.map(({ key, value }) => {
													const valueStars = pctToStars(value);
													return (
														<React.Fragment key={key}>
															<Typography
																variant="caption"
																color="text.secondary"
															>
																{key}
															</Typography>
															<Box
																sx={{
																	display: "inline-flex",
																	alignItems: "center",
																	gap: 0.5,
																}}
															>
																<Rating
																	value={valueStars}
																	max={5}
																	precision={0.5}
																	size="small"
																	readOnly
																	sx={{
																		"& .MuiRating-iconFilled": {
																			color: scoreColor(value ?? 0),
																		},
																		"& .MuiRating-iconEmpty": {
																			color: "#ddd",
																		},
																	}}
																/>
																<Typography variant="caption">
																	{valueStars != null
																		? `${valueStars.toFixed(1)}/5`
																		: "—"}
																</Typography>
															</Box>
														</React.Fragment>
													);
												})}
											</Box>
										}
									>
										<Box sx={{ display: "inline-flex" }}>
											<Rating
												value={stars}
												max={5}
												precision={0.5}
												size="medium"
												readOnly
												sx={{
													"& .MuiRating-iconFilled": {
														color: scoreColor(scorePct ?? 0),
													},
													"& .MuiRating-iconEmpty": { color: "#ddd" },
												}}
											/>
										</Box>
									</Tooltip>
									<Typography variant="h6" fontWeight={700} color="text.primary">
										{scorePct?.toFixed(1) ?? "—"}%
									</Typography>
								</Box>
								<Box sx={{ flexGrow: 1 }}>
									<Typography variant="caption" color="text.secondary">
										Average of last{" "}
										{score?.window_weeks ?? "—"}{" "}
										{(score?.window_weeks ?? 0) === 1 ? "week" : "weeks"}
									</Typography>
									{Array.isArray(score?.weeks_considered) &&
										score.weeks_considered.length > 0 && (
											<Typography variant="caption" color="text.secondary" display="block">
												{score.weeks_considered.join(", ")}
											</Typography>
										)}
									{typeof score?.sample_count === "number" && (
										<Typography variant="caption" color="text.secondary" display="block">
											Sample size: {score.sample_count}
										</Typography>
									)}
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</CardContent>
		</Card>
	);
}

